import React, { useId, createContext, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

/* ------------------------------------------------------------------ */
/* RadioGroup                                                          */
/* ------------------------------------------------------------------ */

interface RadioGroupContextValue {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  onChange,
  label,
  description,
  disabled,
  className = '',
  children,
}) => {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, disabled }}>
      <fieldset
        className={twMerge('border-none p-0 m-0', className)}
        disabled={disabled}
      >
        {label && (
          <legend className="text-sm font-semibold text-text-primary mb-1">
            {label}
          </legend>
        )}
        {description && (
          <p className="text-xs text-text-tertiary mb-3 mt-0">{description}</p>
        )}
        <div className="flex flex-col gap-1.5" role="radiogroup">
          {children}
        </div>
      </fieldset>
    </RadioGroupContext.Provider>
  );
};

RadioGroup.displayName = 'RadioGroup';

/* ------------------------------------------------------------------ */
/* RadioOption                                                         */
/* ------------------------------------------------------------------ */

export interface RadioOptionProps {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export const RadioOption: React.FC<RadioOptionProps> = ({
  value,
  label,
  description,
  disabled: optionDisabled,
  className = '',
}) => {
  const autoId = useId();
  const ctx = useContext(RadioGroupContext);
  if (!ctx) throw new Error('RadioOption must be used inside RadioGroup');

  const isSelected = ctx.value === value;
  const isDisabled = optionDisabled || ctx.disabled;

  return (
    <label
      htmlFor={autoId}
      className={twMerge(
        'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
        isSelected ? 'bg-[var(--theme-primary-light)]' : 'hover:bg-[var(--theme-bg-tertiary)]',
        isDisabled ? 'cursor-not-allowed opacity-60' : '',
        className,
      )}
    >
      <input
        type="radio"
        id={autoId}
        name={ctx.name}
        value={value}
        checked={isSelected}
        onChange={() => ctx.onChange(value)}
        disabled={isDisabled}
        className="mt-0.5 shrink-0 accent-[var(--theme-primary)] cursor-pointer disabled:cursor-not-allowed"
      />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-text-primary">{label}</span>
        {description && (
          <span className="text-[13px] text-text-secondary">{description}</span>
        )}
      </div>
    </label>
  );
};

RadioOption.displayName = 'RadioOption';
